"use client";

import { useEffect, useId, useRef, useState } from "react";
import noUiSlider, { type API } from "nouislider";

/**
 * Fortschrittsschieber (Musterkatalog M-011, Teil 2a; Design-Guide `fortschritt.md`): ein Wert zwischen `min` und `max`
 * (Prozent einer Teilaufgabe, Stand eines Zählers). Gestaltung nach dem Katalogblock `range-slider` (Preline Pro,
 * noUiSlider), mit hellen Theme-Tokens und ohne Schalter; Betreiber-Entscheid 08.10.2026 (1E, neue Abhängigkeit noUiSlider,
 * Abweichung AW-011).
 *
 * Bedienung: Ziehen oder Tippen auf die Leiste; Pfeiltasten ±Schritt, Bild hoch/runter ±`seiteSchritte` Schritte,
 * Pos1/Ende an die Grenzen. Die Griffe tragen `role="slider"` mit `aria-valuenow/min/max`, `aria-label` und
 * `aria-valuetext` mit Einheit (`einheit`). Der Wert steht immer sichtbar daneben.
 *
 * Speichern (`onBestaetigen`): beim Loslassen sofort, bei Tastatur nach 400 ms Ruhe (ein Speichervorgang je Wert), beim
 * Verlassen sofort. Ändert der Aufrufer `wert` (Server-Stand, Rücknahme nach Fehler), folgt der Griff. `deaktiviert`
 * sperrt den Schieber, der Fokus kehrt beim Entsperren zurück. Der Baustein prüft keine Rechte.
 */
const RUHE_MS = 400;

// Abweichung AW-011: Klassen für noUiSlider (cssPrefix leer), nur Theme-Tokens; Griff 32 px, Touch-Fläche 44 px.
const KLASSEN = {
  // Spur (Betreiber-Entscheid 08.10.2026, 2B; Rand 2a "wie Du denkst": dunklerer Rand für ausreichenden UI-Kontrast): hellere Spur `line-3` mit feinem Rand `line-5` (oben/unten am Ziel, an den Kappen als Pillenrand).
  // Die Kappen liegen mit `-top-px` auf der Außenkante des Ziels, damit der Rand lückenlos läuft.
  target: "relative h-8 mx-4 bg-line-3 border-y border-line-5",
  base: "size-full relative z-1 top-0 inset-e-0 before:absolute before:-inset-s-4 before:-top-px before:h-8 before:w-4 before:rounded-s-full before:bg-primary before:border-y before:border-s before:border-line-5 after:absolute after:-inset-e-4 after:-top-px after:h-8 after:w-4 after:rounded-e-full after:bg-line-3 after:border-y after:border-e after:border-line-5",
  origin: "absolute top-0 inset-e-0 size-full origin-top-left",
  handle:
    "absolute top-1/2 inset-e-0 z-2 flex justify-center items-center size-8 bg-layer border border-layer-line shadow-sm rounded-full cursor-pointer translate-x-2/4 -translate-y-2/4 text-foreground focus:outline-hidden focus:ring-2 focus:ring-primary-focus",
  connects: "relative z-0 size-full overflow-hidden",
  connect: "absolute top-0 inset-e-0 z-1 size-full bg-primary origin-top-left",
  touchArea: "absolute -inset-1.5",
};

/** Lucide `chevrons-left-right` als Griff-Symbol (statisches, eigenes Markup). */
const GRIFF_SYMBOL =
  '<svg class="shrink-0 size-4 pointer-events-none" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 7-5 5 5 5"/><path d="m15 7 5 5-5 5"/></svg>';

const NAVIGATIONSTASTEN = new Set(["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End"]);

export function Fortschrittsschieber({
  id: idVorgabe,
  label,
  wert,
  min = 0,
  max = 100,
  schritt = 1,
  seiteSchritte = 5,
  einheit,
  deaktiviert = false,
  onBestaetigen,
}: {
  id?: string;
  /** Beschriftung (`aria-label` des Griffs). */
  label: string;
  wert: number;
  min?: number;
  max?: number;
  schritt?: number;
  /** Bild hoch/runter springt um so viele Schritte. */
  seiteSchritte?: number;
  /** Wertanzeige mit Einheit („40 %“, „2/4“); sie steht sichtbar daneben und im `aria-valuetext`. */
  einheit?: (aktuell: number) => string;
  deaktiviert?: boolean;
  /** Liefert der Aufrufer ein Versprechen und es ergibt `false` (Speichern gescheitert), nimmt der Schieber auf den Wert des Aufrufers zurück. */
  onBestaetigen: (neu: number) => void | Promise<boolean | void>;
}) {
  const erzeugt = useId();
  const id = idVorgabe ?? erzeugt;
  const wurzel = useRef<HTMLDivElement | null>(null);
  const ziel = useRef<HTMLDivElement | null>(null);
  const api = useRef<API | null>(null);
  const [anzeige, setAnzeige] = useState(wert);
  const aktuell = useRef(wert);
  const letzter = useRef(wert);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ausTaste = useRef(false);
  const meldung = useRef(onBestaetigen);
  const text = useRef(einheit);
  const hatteFokus = useRef(false);
  const wertRef = useRef(wert);
  useEffect(() => {
    meldung.current = onBestaetigen;
    text.current = einheit;
  });

  useEffect(() => {
    const knoten = ziel.current;
    const huelle = wurzel.current;
    if (!knoten || !huelle) return;
    const beschriftung = (v: number) => (text.current ? text.current(Math.round(v)) : String(Math.round(v)));
    const s = noUiSlider.create(knoten, {
      start: aktuell.current,
      connect: "lower",
      range: { min, max },
      step: schritt,
      cssPrefix: "",
      cssClasses: { ...noUiSlider.cssClasses, ...KLASSEN },
      keyboardSupport: true,
      keyboardPageMultiplier: seiteSchritte,
      keyboardMultiplier: 1,
      behaviour: "tap",
      format: { to: (v: number) => String(Math.round(v)), from: (v: string) => Number(v) },
      ariaFormat: { to: (v: number) => beschriftung(v) },
      handleAttributes: [{ "aria-label": label }],
    });
    api.current = s;
    const griff = knoten.querySelector<HTMLElement>("[role='slider']");
    griff?.insertAdjacentHTML("beforeend", GRIFF_SYMBOL);

    const speichere = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
      if (aktuell.current === letzter.current) return;
      letzter.current = aktuell.current;
      const antwort = meldung.current(aktuell.current);
      if (antwort instanceof Promise) {
        void antwort.then((ok) => {
          if (ok !== false) return;
          // Gescheitert: zurück auf den Stand des Aufrufers, auch wenn sich dessen Wert nicht geändert hat (gebündeltes Rendern).
          letzter.current = wertRef.current;
          aktuell.current = wertRef.current;
          api.current?.set(wertRef.current, false);
          setAnzeige(wertRef.current);
        });
      }
    };
    s.on("update", (werte) => {
      const v = Math.round(Number(werte[0]));
      aktuell.current = v;
      setAnzeige(v);
    });
    s.on("change", () => {
      if (ausTaste.current) {
        ausTaste.current = false;
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(speichere, RUHE_MS);
      } else {
        speichere();
      }
    });
    // Capture am Umschlag: läuft vor dem eigenen Tastenhandler des Griffs und merkt sich, dass die Änderung von der Tastatur kommt.
    const beiTaste = (e: KeyboardEvent) => {
      if (NAVIGATIONSTASTEN.has(e.key)) ausTaste.current = true;
    };
    const beiVerlassen = () => {
      if (timer.current) speichere();
    };
    huelle.addEventListener("keydown", beiTaste, true);
    huelle.addEventListener("focusout", beiVerlassen);
    return () => {
      huelle.removeEventListener("keydown", beiTaste, true);
      huelle.removeEventListener("focusout", beiVerlassen);
      if (timer.current) speichere();
      s.destroy();
      api.current = null;
    };
    // label und Grenzen ändern sich im Betrieb nicht; ein Wechsel baut den Schieber neu auf.
  }, [min, max, schritt, seiteSchritte, label]);

  // Der Aufrufer ändert den Wert (Server-Stand, Rücknahme nach Fehler): der Griff folgt, nichts wird gemeldet.
  useEffect(() => {
    wertRef.current = wert;
    letzter.current = wert;
    aktuell.current = wert;
    api.current?.set(wert, false);
    setAnzeige(wert);
  }, [wert]);

  // Sperre während der Speicherung; der Fokus bleibt dem Schieber erhalten, auch wenn die Sperre den Griff kurz entfernt.
  useEffect(() => {
    const s = api.current;
    const huelle = wurzel.current;
    if (!s || !huelle) return;
    if (deaktiviert) {
      hatteFokus.current = huelle.contains(document.activeElement);
      s.disable();
    } else {
      s.enable();
      if (hatteFokus.current) {
        huelle.querySelector<HTMLElement>("[role='slider']")?.focus();
        hatteFokus.current = false;
      }
    }
  }, [deaktiviert]);

  const angezeigt = einheit ? einheit(anzeige) : String(anzeige);
  return (
    <>
      {/* Preline: forms/forms-sub-filters-refinement/forms-cat-range-controls/transparent-range-slider-row-with-toggle (Datei: bloecke/forms/forms-cat-range-controls/transparent-range-slider-row-with-toggle.html; der Katalogpfad führt die Unterbereichsebene forms-sub-filters-refinement, die Datei liegt ohne sie) */}
      <div ref={wurzel} id={id} className={deaktiviert ? "flex items-center gap-x-3 opacity-50" : "flex items-center gap-x-3"} aria-busy={deaktiviert}>
        <div ref={ziel} className="grow" />
        <span className="shrink-0 min-w-14 text-end text-sm tabular-nums text-foreground" aria-hidden="true">
          {angezeigt}
        </span>
      </div>
    </>
  );
}
